import { Radio, radioClasses, RadioGroup, styled } from '@healthinal/ui';

const DURATION_IN_DAYS_OPTIONS = [7, 14, 30, 90] as const;
const DURATION_IN_DAYS_OPTIONS_WITH_OPEN_END = [...DURATION_IN_DAYS_OPTIONS, null];

export type DurationInDays = (typeof DURATION_IN_DAYS_OPTIONS)[number];
export type DurationInDaysNullable = DurationInDays | null;

interface Props<TAllowOpenEnd extends boolean> {
  value: TAllowOpenEnd extends true ? DurationInDaysNullable : DurationInDays;
  onChange: (value: TAllowOpenEnd extends true ? DurationInDaysNullable : DurationInDays) => void;
  allowOpenEnd?: TAllowOpenEnd;
}

export function DurationSelector<TAllowOpenEnd extends boolean>({
  value,
  onChange,
  allowOpenEnd,
}: Props<TAllowOpenEnd>) {
  const options = allowOpenEnd ? DURATION_IN_DAYS_OPTIONS_WITH_OPEN_END : DURATION_IN_DAYS_OPTIONS;
  return (
    <RadioGroup
      value={value === null ? Infinity : value}
      onChange={(event) => {
        const num = Number(event.target.value);
        onChange((isFinite(num) ? num : null) as typeof value);
      }}
      orientation="horizontal"
      variant="outlined"
      sx={{
        '--RadioGroup-gap': 0,
        margin: 0,
      }}>
      {options.map((days) => (
        <StyledRadio
          key={days}
          value={days === null ? Infinity : days}
          label={days === null ? 'Unbefristet' : `${days} Tage`}
          disableIcon
          variant={value === days ? 'soft' : 'outlined'}
          color="neutral"
          slotProps={{
            action: {
              sx: { borderRadius: 0 },
            },
          }}
        />
      ))}
    </RadioGroup>
  );
}

const StyledRadio = styled(Radio)(({ theme }) => ({
  '--variant-softBg': theme.palette.neutral.softHoverBg,
  '--variant-softActiveBg': theme.palette.neutral.softHoverBg,
  '&:not([data-last-child])': {
    borderRight: `solid 1px ${theme.palette.neutral.outlinedBorder}`,
  },
  '&[data-first-child]': {
    [`.${radioClasses.action}`]: {
      borderTopLeftRadius: `calc(${theme.radius.sm} - 1px)`,
      borderBottomLeftRadius: `calc(${theme.radius.sm} - 1px)`,
    },
  },
  '&[data-last-child]': {
    [`.${radioClasses.action}`]: {
      borderTopRightRadius: `calc(${theme.radius.sm} - 1px)`,
      borderBottomRightRadius: `calc(${theme.radius.sm} - 1px)`,
    },
  },
  padding: '0 12px',
  fontSize: theme.fontSize.sm,
  lineHeight: '34px',
  fontWeight: theme.fontWeight.lg,
  whiteSpace: 'nowrap',
}));
